import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FormWrapper from '../Forms/FormWrapper'; // Import corrigé
import Input from '../Forms/Input'; // Import corrigé
import Select from '../Forms/Select'; // Importer Select
import Textarea from '../Forms/Textarea'; // Importer Textarea
import { Domain, UseCaseSummary, UseCaseStatus } from '../../types';
import { Button } from '../Common/button';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import notify from '../../lib/notify';
import useCaseService from '@/services/useCaseService';
import cadrageService from '@/services/cadrageService';
import { domainService } from '@/services/domainService';
import { USER_JOURNEY_COMPLETE_STEP } from '@/config/onboardingTours';
import { useTourStore } from '../../stores/tourStore';

// Interface pour les données du formulaire étendue
interface FormData {
  name: string; // Nom du cadrage
  client: string;
  // Champs Info Société (optionnels)
  company_name?: string;
  company_sector?: string;
  company_size?: string;
  company_revenue?: string;
  company_location?: string;
  main_contact_name?: string;
  main_contact_role?: string;
  main_contact_email?: string;
  main_contact_phone?: string;
  // Champs Cas d'Usage
  useCaseSelection: string; // 'existing' ou 'new' ou ID du cas d'usage existant
  newUseCaseName?: string; // Nom du nouveau cas d'usage (optionnel)
  newUseCaseDescription?: string; // Description du nouveau cas d'usage (optionnel)
}

export default function NewCadrageForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedUseCaseId = searchParams.get('useCaseId');
  
  const [domains, setDomains] = useState<Domain[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [existingUseCases, setExistingUseCases] = useState<UseCaseSummary[]>([]);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState(t('newCadrage.errorTitle'));
  const [errorMessage, setErrorMessage] = useState('');
  // Progressive enrichment: only required fields shown by default; users can
  // opt-in to fill the company contact section at creation time.
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Load user domains on mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const userDomains = await domainService.getUserDomains();
        setDomains(userDomains);
      } catch (error) {
        console.error("Erreur lors de la récupération des domaines:", error);
      }
    };
    fetchDomains();
  }, []);

  // When preselectedUseCaseId is in URL, fetch that use case to get its domain and preselect domain
  useEffect(() => {
    if (!preselectedUseCaseId) return;
    const resolvePreselected = async () => {
      try {
        const uc = await useCaseService.get(preselectedUseCaseId);
        const domainId = (uc as { domain_id?: string }).domain_id ?? null;
        if (domainId) setSelectedDomainId(domainId);
      } catch (error) {
        console.error("Erreur lors de la récupération du cas d'usage présélectionné:", error);
      }
    };
    resolvePreselected();
  }, [preselectedUseCaseId]);

  // Load use cases for the selected domain
  useEffect(() => {
    if (!selectedDomainId) {
      setExistingUseCases([]);
      return;
    }
    const fetchUseCasesForDomain = async () => {
      try {
        const useCases = await useCaseService.list({ domain_id: selectedDomainId });
        const useCasesList = useCases.map((uc: UseCaseSummary) => ({
          id: uc.id,
          name: uc.name,
          status: uc.status,
          short_description: uc.short_description,
          value_score: uc.value_score,
          complexity_score: uc.complexity_score
        }));
        setExistingUseCases(useCasesList);
      } catch (error) {
        console.error("Erreur lors de la récupération des cas d'usage du domaine:", error);
        setExistingUseCases([]);
      }
    };
    fetchUseCasesForDomain();
  }, [selectedDomainId]);


  const onSubmit: SubmitHandler<FormData> = async (data) => { // Rendre la fonction async
    let sourceUseCaseId: string | undefined = undefined;

    // 1. Gérer la création ou la sélection du cas d'usage
    if (data.useCaseSelection === 'new') {
      if (!selectedDomainId) {
        setErrorTitle(t('newCadrage.domainError'));
        setErrorMessage(t('newCadrage.useCaseMissingMessage'));
        setErrorOpen(true);
        return;
      }
      if (!data.newUseCaseName || !data.newUseCaseDescription) {
        console.error("Le nom et la description sont requis pour créer un nouveau cas d'usage.");
        setErrorTitle(t('newCadrage.missingDataTitle'));
        setErrorMessage(t('newCadrage.missingDataMessage'));
        setErrorOpen(true);
        return;
      }
      const newUseCasePayload = {
        name: data.newUseCaseName,
        domain_id: selectedDomainId,
        description: data.newUseCaseDescription,
        short_description: data.newUseCaseDescription.substring(0, 100) + (data.newUseCaseDescription.length > 100 ? '...' : ''),
        status: 'Identified' as UseCaseStatus,
      };
      try {
        const newUseCase = await useCaseService.create(newUseCasePayload);
        sourceUseCaseId = newUseCase.id;
        // audit notion removed
      } catch (error) {
        console.error("Erreur lors de la création du cas d'usage:", error);
        setErrorTitle(t('newCadrage.useCaseCreationErrorTitle'));
        setErrorMessage(t('newCadrage.useCaseCreationErrorMessage'));
        setErrorOpen(true);
        return; // Arrêter la soumission
      }
    } else {
      // Un cas d'usage existant a été sélectionné
      sourceUseCaseId = data.useCaseSelection;
      // Essayer de trouver l'auditId correspondant dans la liste chargée
      // audit notion removed
    }

    // 2. Préparer le payload conforme au backend (CadrageCreate)
    if (!sourceUseCaseId) {
      setErrorTitle(t('newCadrage.useCaseMissingTitle'));
      setErrorMessage(t('newCadrage.useCaseMissingMessage'));
      setErrorOpen(true);
      return;
    }

    const cadragePayload = {
      name: data.name,
      type: 'cadrage', // Champ requis par le backend
      use_case_id: sourceUseCaseId, // Lien obligatoire
      // Champs Info Société optionnels
      company_name: data.company_name || null,
      company_sector: data.company_sector || null,
      company_size: data.company_size || null,
      company_revenue: data.company_revenue || null,
      company_location: data.company_location || null,
      main_contact_name: data.main_contact_name || null,
      main_contact_role: data.main_contact_role || null,
      main_contact_email: data.main_contact_email || null,
      main_contact_phone: data.main_contact_phone || null,
    } as const;

    try {
      // 3. Appeler l'API pour créer le Cadrage
      // Supposons que l'API retourne le Cadrage créé (type CadrageRead ou similaire)
      const newCadrage = await cadrageService.createCadrage(cadragePayload); // Utiliser Project comme type de retour pour l'instant
      const realCadrageId = newCadrage.id;

      // If user is in the "Complete User Journey" tour, advance to the completion step before navigating
      const { activeTourId, setStep } = useTourStore.getState();
      if (activeTourId === 'userJourney') {
        setStep(USER_JOURNEY_COMPLETE_STEP);
      }

      // 4. Pas besoin de lier après coup: use_case_id est envoyé dans le payload

      // 5. Rediriger vers la page du use case si preselectedUseCaseId existe, sinon vers le cadrage
      notify.success(t('newCadrage.success'));
      if (preselectedUseCaseId) {
        navigate(`/cas-usage/${preselectedUseCaseId}`);
      } else {
        navigate(`/cadrages/${realCadrageId}`);
      }

    } catch (error) {
      console.error("Erreur lors de la création du cadrage:", error);
      setErrorTitle(t('newCadrage.cadrageCreationErrorTitle'));
      setErrorMessage(t('newCadrage.cadrageCreationErrorMessage'));
      setErrorOpen(true);
      // Ne pas naviguer en cas d'erreur de création
    }

    // Supprimer l'appel au store local car la création se fait via API
    // addCadrage(newCadrage);
  };

  // Utilisation de FormWrapper avec le pattern render prop
  return (
    <div className="space-y-6">
      <FormWrapper<FormData>
        onSubmit={onSubmit}
        defaultValues={{
          name: '',
          client: '',
          company_name: '',
          company_sector: '',
          company_size: '',
          company_revenue: '',
          company_location: '',
          main_contact_name: '',
          main_contact_role: '',
          main_contact_email: '',
          main_contact_phone: '',
          useCaseSelection: '',
          newUseCaseName: '',
          newUseCaseDescription: '',
        }}
      >
        {(methods) => {
          const useCaseSelectionValue = useWatch({
            control: methods.control,
            name: 'useCaseSelection',
          });
          const isCreatingNewUseCase = useCaseSelectionValue === 'new';

          // Mettre à jour la valeur du select quand les use cases sont chargés et qu'un use case est présélectionné
          useEffect(() => {
            if (preselectedUseCaseId && existingUseCases.length > 0) {
              const useCaseExists = existingUseCases.find(uc => uc.id === preselectedUseCaseId);
              if (useCaseExists && useCaseSelectionValue !== preselectedUseCaseId) {
                methods.setValue('useCaseSelection', preselectedUseCaseId);
              }
            }
          }, [existingUseCases, preselectedUseCaseId, useCaseSelectionValue, methods]);

          return (
            <div>
              <div className="bg-card rounded-lg border border-border shadow-sm p-6 space-y-6">
                {/* Section Informations Cadrage */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                    {t('newCadrage.cadrageInfo')}
                  </h2>
                  <div className="space-y-4">
                    <div data-tour="cadrage-name-field">
                      <Input
                        label={t('newCadrage.nameLabel')}
                        name="name"
                        register={methods.register}
                        error={methods.formState.errors.name}
                      />
                    </div>
                    <div data-tour="cadrage-client-field">
                      <Input
                        label={t('newCadrage.clientLabel')}
                        name="client"
                        register={methods.register}
                        error={methods.formState.errors.client}
                      />
                    </div>
                  </div>
                </div>

                {/* Section Informations Société (optionnelle, repliée par défaut) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((v) => !v)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    {showAdvanced ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    {showAdvanced
                      ? t('newCadrage.hideOptional', 'Masquer les détails optionnels')
                      : t('newCadrage.showOptional', 'Ajouter le contact principal (optionnel)')}
                  </button>
                  {!showAdvanced && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t(
                        'newCadrage.progressiveHint',
                        'Vous pourrez compléter ces informations après la création — un indicateur de complétude vous y invitera.'
                      )}
                    </p>
                  )}
                </div>

                {showAdvanced && (
                  <div className="space-y-4" data-tour="cadrage-company-info">
                    <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                      {t('newCadrage.companyInfo')}
                    </h2>
                    <div>
                      <h4 className="text-base font-medium text-foreground mb-4">{t('newCadrage.mainContact')}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label={t('newCadrage.contactNameLabel')}
                          name="main_contact_name"
                          register={methods.register}
                          error={methods.formState.errors.main_contact_name}
                        />
                        <Input
                          label={t('newCadrage.contactRoleLabel')}
                          name="main_contact_role"
                          register={methods.register}
                          error={methods.formState.errors.main_contact_role}
                        />
                        <Input
                          label={t('newCadrage.contactEmailLabel')}
                          name="main_contact_email"
                          type="email"
                          register={methods.register}
                          error={methods.formState.errors.main_contact_email}
                        />
                        <Input
                          label={t('newCadrage.contactPhoneLabel')}
                          name="main_contact_phone"
                          type="tel"
                          register={methods.register}
                          error={methods.formState.errors.main_contact_phone}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Section Cas d'Usage Associé */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                    {t('newCadrage.associatedUseCase')}
                  </h2>
                  <div className="space-y-4">
                    <div data-tour="cadrage-domain-selector">
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {t('newUseCase.domainLabel')}
                      </label>
                      <select
                        value={selectedDomainId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedDomainId(v || null);
                          methods.setValue('useCaseSelection', '');
                        }}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:text-sm"
                      >
                        <option value="">{t('newCadrage.selectDomain')}</option>
                        {domains.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </div>
                    <div data-tour="cadrage-usecase-selector">
                      <Select
                        label={t('newCadrage.selectOrCreate')}
                        name="useCaseSelection"
                        register={methods.register}
                        error={methods.formState.errors.useCaseSelection}
                        options={[
                          { value: '', label: t('newCadrage.selectUseCase') },
                          ...existingUseCases.map(uc => ({ value: uc.id, label: uc.name })),
                          { value: 'new', label: t('newCadrage.createNew') }
                        ]}
                        disabled={!selectedDomainId}
                      />
                    </div>

                    {/* Champs conditionnels pour la création */}
                    {isCreatingNewUseCase && (
                      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
                        <p className="text-sm text-muted-foreground mb-4">
                          {t('newCadrage.newUseCaseDetails')}
                        </p>
                        <div className="space-y-4">
                          <Input
                            label={t('newCadrage.newUseCaseNameLabel')}
                            name="newUseCaseName"
                            register={methods.register}
                            error={methods.formState.errors.newUseCaseName}
                            rules={{ required: isCreatingNewUseCase ? t('newCadrage.newUseCaseNameRequired') : false }}
                          />
                          <Textarea
                            label={t('newCadrage.newUseCaseDescriptionLabel')}
                            name="newUseCaseDescription"
                            register={methods.register}
                            error={methods.formState.errors.newUseCaseDescription}
                            rows={4}
                            rules={{ required: isCreatingNewUseCase ? t('newCadrage.newUseCaseDescriptionRequired') : false }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (preselectedUseCaseId) {
                        navigate(`/cas-usage/${preselectedUseCaseId}`);
                      } else {
                        navigate('/cadrages');
                      }
                    }}
                    disabled={methods.formState.isSubmitting}
                  >
                    {t('global.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={methods.formState.isSubmitting}
                    className="min-w-[140px]"
                    data-tour="cadrage-submit-btn"
                  >
                    {methods.formState.isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('global.creating')}
                      </div>
                    ) : (
                      t('newCadrage.create')
                    )}
                  </Button>
                </div>
              </div>
            </div>
          );
        }}
      </FormWrapper>

      <ConfirmationModal
        open={errorOpen}
        title={errorTitle}
        message={errorMessage}
        variant="warning"
        confirmText="OK"
        onConfirm={() => setErrorOpen(false)}
        onCancel={() => setErrorOpen(false)}
      />

      {/* Help Section */}
      <div>
        <div className="bg-muted rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">
            {t('newCadrage.aboutTitle')}
          </h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              {t('newCadrage.aboutDescription')}
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>{t('newCadrage.aboutPoint1')}</li>
              <li>{t('newCadrage.aboutPoint2')}</li>
              <li>{t('newCadrage.aboutPoint3')}</li>
              <li>{t('newCadrage.aboutPoint4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
